import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import { catchError } from "rxjs/operators";
import { AppSettingsService } from "./../app-settings.service";
import { ViewStateService } from "./../view-state.service";

@Injectable()
export class PortalApiService {
  private baseUrl: string;

  constructor(
    private cookieService: CookieService,
    private appSettings: AppSettingsService,
    private angularHttp: HttpClient,
    private viewState: ViewStateService,
    private router: Router
  ) {
    this.baseUrl = this.appSettings.PortalApiBaseUrl;
  }

  private setHeaders(): HttpHeaders {
    const token = this.getAuthorizationToken();
    let headers = new HttpHeaders({
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers":
        "Origin, X-Requested-With, Content-Type, Accept, Authorization",
    });

    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  }

  get(endpoint: string, queryParams: any = null): Observable<any> {
    const url = this.baseUrl.concat(endpoint);
    const headers = this.setHeaders();

    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach((key) => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.angularHttp
      .get(url, { headers, params, responseType: "json" })
      .pipe(catchError((error) => this.handleError(error)));
  }

  getActiveDirectory(endpoint: string, queryParams: any = null): Observable<any> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.setHeaders();

    return this.angularHttp
      .get(`${url}?${queryParams}`, { headers, params: queryParams })
      .pipe(catchError((error) => this.handleError(error)));
  }

  getAllDirectory(endpoint: string, queryParams: any = null): Observable<any> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = this.setHeaders();

    return this.angularHttp
      //.get(url, { headers, params: queryParams })
      .get(`${url}?${queryParams}`, { headers, params: queryParams })
      .pipe(catchError((error) => this.handleError(error)));
  }

  getDocumentStream(endpoint: string): Observable<Blob> {
    const url = this.baseUrl.concat(endpoint);
    const headers = this.setHeaders();

    return this.angularHttp
      .get(url, { headers, responseType: "blob" })
      .pipe(catchError((error) => this.handleError(error)));
  }

  getBlob(endpoint: string, queryParams: any = null): Observable<Blob> {
    const url = this.baseUrl.concat(endpoint);
    const headers = this.setHeaders();

    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach((key) => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.angularHttp
      .get(url, { headers, params, responseType: "blob" })
      .pipe(catchError((error) => this.handleError(error)));
  }

  post(endpoint: string, data: any = null, asBlob: boolean = false): Observable<any> {
    const url = this.baseUrl.concat(endpoint);
    const headers = this.setHeaders();
  
    // Define options object with responseType set correctly based on `asBlob` parameter
    const options = {
      headers,
      responseType: asBlob ? 'blob' : 'json'  // Use 'blob' or 'json' explicitly
    };
  
    return this.angularHttp
      .post(url, data, options as any)  // Pass the correct options to HttpClient's post method
      .pipe(catchError((error) => this.handleError(error)));
  }

  put(endpoint: string, data: any = null): Observable<any> {
    const url = this.baseUrl.concat(endpoint);
    const headers = this.setHeaders();

    return this.angularHttp
      .put(url, data, { headers })
      .pipe(catchError((error) => this.handleError(error)));
  }

  delete(endpoint: string): Observable<any> {
    const url = this.baseUrl.concat(endpoint);
    const headers = this.setHeaders();

    return this.angularHttp
      .delete(url, { headers })
      .pipe(catchError((error) => this.handleError(error)));
  }

  private handleUnauthorizedResponse() {
    this.viewState.stopLoading();
    this.router.navigate(["auth/sign-out"]);
  }

  private handleError(error: any): Observable<never> {
    if (error.status === 401) {
      this.handleUnauthorizedResponse();
    }
    return throwError(error);
  }

  private getAuthorizationToken(): string | null {
    const customCookieName = localStorage.getItem("customCookieName");
    return customCookieName ? this.cookieService.get(customCookieName) : null;
  }
}